import { useEffect, useState, type ChangeEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { formatMoney } from '../../lib/format.js';
import type { LegalEntity, LegalEntityUpsert, PaymentMode } from '@arcnet/contracts';
import {
  useLegalEntity, useSaveLegalEntity, usePayfastSettings, useSavePayfastSettings,
} from '../../lib/api/hooks-payments.js';
import { ErrorBanner, Segmented, Badge } from '../../components/ui/Primitives.js';
import { ApiClientError } from '../../lib/api/client.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';

/**
 * Billing setup (docs/28 §3): ArcNet's legal entity, the PayFast merchant the money
 * goes to, and the address billing email is sent from.
 *
 * Reachable from an admin address only (nginx), and every save asks for the
 * person's password again: the merchant decides where every client's money
 * lands, which makes it the setting on the platform most worth stealing.
 */

const missing = (e: unknown) => e instanceof ApiClientError && e.status === 404;

export function BillingEntityPage() {
  const entity = useLegalEntity();

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Billing setup</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Who clients pay, the PayFast account the money goes to, and where billing email comes from
          </p>
        </div>
      </div>

      {entity.data === undefined ? (
        <div className="arc-card arc-card-body"><div className="arc-skeleton" style={{ height: 160 }} /></div>
      ) : entity.error && !missing(entity.error) ? (
        <ErrorBanner error={entity.error} />
      ) : (
        <>
          <EntityCard saved={entity.data ?? null} />
          <PayfastCard entitySaved={!!entity.data} />
          <PricesCard />
          {/* The Resend key moved to Platform > Provider keys, with the other providers'
              (owner, 20 September 2026). PayFast stays: it is a merchant account for THIS
              entity, not a key on its own. */}
        </>
      )}
    </>
  );
}

type EntityForm = Record<keyof LegalEntityUpsert, string>;

const ENTITY_FIELDS: ReadonlyArray<readonly [Exclude<keyof LegalEntityUpsert, 'address'>, string]> = [
  ['registered_name', 'Registered name'],
  ['trading_name', 'Trading name'],
  ['registration_number', 'Registration number'],
  ['vat_number', 'VAT number'],
  ['email', 'Email'],
  ['phone', 'Phone'],
];

const formFrom = (e: LegalEntity | null): EntityForm => ({
  registered_name: e?.registered_name ?? '',
  trading_name: e?.trading_name ?? '',
  registration_number: e?.registration_number ?? '',
  vat_number: e?.vat_number ?? '',
  address: e?.address ?? '',
  email: e?.email ?? '',
  phone: e?.phone ?? '',
  bank_name: e?.bank_name ?? '',
  bank_account_name: e?.bank_account_name ?? '',
  bank_account_number: e?.bank_account_number ?? '',
  bank_branch_code: e?.bank_branch_code ?? '',
  payment_terms_days: String(e?.payment_terms_days ?? 7),
});

const BANK_FIELDS: ReadonlyArray<readonly [
  'bank_name' | 'bank_account_name' | 'bank_account_number' | 'bank_branch_code', string,
]> = [
  ['bank_account_name', 'Account name'],
  ['bank_name', 'Bank'],
  ['bank_account_number', 'Account number'],
  ['bank_branch_code', 'Branch code'],
];

/** Blank clears an optional field; it is never saved as an empty string. */
const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

function EntityCard({ saved }: { saved: LegalEntity | null }) {
  const save = useSaveLegalEntity();
  const step = useStepUp();
  const [form, setForm] = useState<EntityForm>(() => formFrom(saved));
  useEffect(() => { setForm(formFrom(saved)); }, [saved]);

  const err = save.error instanceof ApiClientError ? save.error : null;
  const fieldErrors = err?.fieldErrors ?? {};
  const set = (k: keyof EntityForm) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = () => void step.run(() => save.mutateAsync({
    registered_name: form.registered_name.trim(),
    trading_name: orNull(form.trading_name),
    registration_number: orNull(form.registration_number),
    vat_number: orNull(form.vat_number),
    address: orNull(form.address),
    email: orNull(form.email),
    phone: orNull(form.phone),
    bank_name: orNull(form.bank_name),
    bank_account_name: orNull(form.bank_account_name),
    bank_account_number: orNull(form.bank_account_number),
    bank_branch_code: orNull(form.bank_branch_code),
    payment_terms_days: Number(form.payment_terms_days) || 0,
  }));

  return (
    <div className="arc-card" style={{ marginBottom: 14 }}>
      <div className="arc-card-head">
        <h2 className="arc-h2">Legal entity</h2>
      </div>
      <form className="arc-card-body" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <StepUpPrompt step={step} what="Changing the billing entity" />

        <div className="arc-grid arc-grid-2">
          {ENTITY_FIELDS.map(([k, label]) => (
            <div key={k} className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor={`entity-${k}`}>{label}</label>
              <input id={`entity-${k}`} className="arc-input" value={form[k]} onChange={set(k)}
                type={k === 'email' ? 'email' : 'text'} required={k === 'registered_name'}
                aria-invalid={!!fieldErrors[k]} />
              {fieldErrors[k] && <div className="arc-error">{fieldErrors[k]}</div>}
            </div>
          ))}
        </div>
        <div className="arc-field" style={{ marginTop: 14 }}>
          <label className="arc-label" htmlFor="entity-address">Address</label>
          <textarea id="entity-address" className="arc-input" rows={3} value={form.address}
            onChange={set('address')} aria-invalid={!!fieldErrors['address']} />
          {fieldErrors['address'] && <div className="arc-error">{fieldErrors['address']}</div>}
        </div>

        <div className="arc-label" style={{ margin: '4px 0 8px' }}>Where invoices are paid</div>
        <div className="arc-grid arc-grid-2" style={{ marginBottom: 14 }}>
          {BANK_FIELDS.map(([k, label]) => (
            <div key={k} className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor={`entity-${k}`}>{label}</label>
              <input id={`entity-${k}`} className="arc-input" value={form[k]} onChange={set(k)}
                inputMode={k === 'bank_account_number' || k === 'bank_branch_code' ? 'numeric' : undefined}
                aria-invalid={!!fieldErrors[k]} />
              {fieldErrors[k] && <div className="arc-error">{fieldErrors[k]}</div>}
            </div>
          ))}
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="entity-terms">Days to pay</label>
            <input id="entity-terms" className="arc-input arc-num" inputMode="numeric"
              value={form.payment_terms_days} onChange={set('payment_terms_days')}
              aria-invalid={!!fieldErrors['payment_terms_days']} />
            {fieldErrors['payment_terms_days']
              ? <div className="arc-error">{fieldErrors['payment_terms_days']}</div>
              : <div className="arc-hint">From an invoice&apos;s date to its due date.</div>}
          </div>
        </div>

        <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </form>
    </div>
  );
}

function PayfastCard({ entitySaved }: { entitySaved: boolean }) {
  const settings = usePayfastSettings();
  const save = useSavePayfastSettings();
  const step = useStepUp();
  const saved = settings.data ?? null;

  const [mode, setMode] = useState<PaymentMode>('sandbox');
  const [merchantId, setMerchantId] = useState('');
  const [merchantKey, setMerchantKey] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [enabled, setEnabled] = useState(false);

  // A save comes back as the saved state. The secrets are never in it, so
  // their boxes go back to empty.
  useEffect(() => {
    if (!saved) return;
    setMode(saved.mode);
    setMerchantId(saved.merchant_id);
    setEnabled(saved.enabled);
    setMerchantKey('');
    setPassphrase('');
  }, [saved]);

  const err = save.error instanceof ApiClientError ? save.error : null;
  const fieldErrors = err?.fieldErrors ?? {};

  const submit = () => void step.run(() => save.mutateAsync({
    mode,
    merchant_id: merchantId.trim(),
    enabled,
    ...(merchantKey.trim() ? { merchant_key: merchantKey.trim() } : {}),
    ...(passphrase ? { passphrase } : {}),
  }));

  const replace = 'Saved. Type a new one to replace it';

  return (
    <div className="arc-card" style={{ marginBottom: 14 }}>
      <div className="arc-card-head">
        <h2 className="arc-h2">PayFast</h2>
        {saved && (saved.enabled
          ? (
            <Badge tone={saved.mode === 'live' ? 'ok' : 'info'}>
              {saved.mode === 'live' ? 'taking payments' : 'taking test payments'}
            </Badge>
          )
          : <Badge tone="muted">off</Badge>)}
      </div>

      {settings.error && !missing(settings.error) ? (
        <div className="arc-card-body"><ErrorBanner error={settings.error} /></div>
      ) : (
        <form className="arc-card-body" onSubmit={(e) => { e.preventDefault(); submit(); }}>
          <StepUpPrompt step={step} what="Changing where payments go" />
          {!entitySaved && (
            <p className="arc-muted" style={{ marginTop: 0 }}>
              Save the legal entity above first: payments are received in its name.
            </p>
          )}

          <div className="arc-field">
            <span className="arc-label">Mode</span>
            <Segmented label="Mode" value={mode} onChange={setMode}
              options={[['sandbox', 'Sandbox'], ['live', 'Live']] as const} />
          </div>

          <div className="arc-grid arc-grid-2">
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="payfast-merchant-id">Merchant ID</label>
              <input id="payfast-merchant-id" className="arc-input arc-mono" inputMode="numeric"
                value={merchantId} onChange={(e) => setMerchantId(e.target.value)} required
                aria-invalid={!!fieldErrors['merchant_id']} />
              {fieldErrors['merchant_id'] && <div className="arc-error">{fieldErrors['merchant_id']}</div>}
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="payfast-merchant-key">Merchant key</label>
              <input id="payfast-merchant-key" className="arc-input arc-mono" type="password"
                autoComplete="off" value={merchantKey} onChange={(e) => setMerchantKey(e.target.value)}
                required={!saved} placeholder={saved ? replace : undefined}
                aria-invalid={!!fieldErrors['merchant_key']} />
              {fieldErrors['merchant_key'] && <div className="arc-error">{fieldErrors['merchant_key']}</div>}
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="payfast-passphrase">Passphrase</label>
              <input id="payfast-passphrase" className="arc-input" type="password"
                autoComplete="new-password" value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                placeholder={saved?.passphrase_set ? replace : undefined}
                aria-invalid={!!fieldErrors['passphrase']} />
              {fieldErrors['passphrase']
                ? <div className="arc-error">{fieldErrors['passphrase']}</div>
                : <div className="arc-hint">The same as in PayFast. Live payments and auto top-up need one.</div>}
            </div>
          </div>

          <label className="arc-row" style={{ gap: 7, margin: '14px 0' }}>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span>Take payments</span>
          </label>

          <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending || !entitySaved}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </form>
      )}
    </div>
  );
}

/**
 * What the platform charges per use, beside the rate sheets (0135): the AI answering-machine check
 * first. Saving asks for the person's password again, like every billing change.
 */
function PricesCard() {
  const qc = useQueryClient();
  const prices = useQuery({ queryKey: ['prices'], queryFn: () => api.get<{ data: Array<{ key: string; label: string; amount: string; currency: string; updated_at: string }> }>('/v1/prices') });
  const step = useStepUp();
  const [edit, setEdit] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<string | null>(null);
  const save = async (key: string) => {
    const amount = (edit[key] ?? '').trim();
    await step.run(async () => {
      await api.put(`/v1/platform/prices/${key}`, { amount });
      setSaved(key);
      setEdit((e) => { const n = { ...e }; delete n[key]; return n; });
      void qc.invalidateQueries({ queryKey: ['prices'] });
    });
  };
  const rows = prices.data?.data ?? [];
  if (rows.length === 0) return null;
  return (
    <div className="arc-card" style={{ marginTop: 14 }}>
      <div className="arc-card-head"><h2 className="arc-h2">Prices per use</h2><span className="arc-subtle">charged to a client's balance each time</span></div>
      <div className="arc-card-body arc-stack" style={{ gap: 10 }}>
        <StepUpPrompt step={step} what="Changing a price" />
        {rows.map((p) => {
          const v = edit[p.key];
          const valid = v === undefined || /^\d{1,6}(\.\d{1,6})?$/.test(v.trim());
          return (
            <div key={p.key} className="arc-row" style={{ gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 550, minWidth: 220 }}>{p.label}</span>
              <input className="arc-input arc-num" style={{ width: 120 }} inputMode="decimal" aria-label={`${p.label} price`}
                value={v ?? String(Number(p.amount))} onChange={(e) => { setSaved(null); setEdit({ ...edit, [p.key]: e.target.value.replace(/[^\d.]/g, '') }); }} />
              <span className="arc-subtle">{p.currency}</span>
              <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={v === undefined || !valid || Number(v) === Number(p.amount)}
                onClick={() => void save(p.key)}>Save</button>
              <span className="arc-subtle" style={{ fontSize: 12 }}>{saved === p.key ? 'Saved' : `now ${formatMoney(p.amount, p.currency)} a check`}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
